import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PanelLeft,
  Search,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  NAVIGATION,
  SETTINGS_NAVIGATION,
  activeSection,
  allowedSections,
  firstSettingsDestination,
  isActivePath,
  type NavItem,
  type NavSection,
} from '@/app/navigation';
import { ui, useUi } from '@/app/ui-store';
import { useSession } from '@/auth/session-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Kbd, ModKey } from '@/components/ui/Kbd';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import { useTheme } from '@/lib/theme';
import { AccountMenu } from './AccountMenu';
import { LogoMark } from './LogoMark';

type NavGroup = NavSection & { label: string; icon: LucideIcon };

const isGroup = (section: NavSection): section is NavGroup =>
  section.label !== undefined && section.icon !== undefined;

/* A rail item: 30px, Sharp corners, weight 500; the current one on the ink fill. */
export const ROW =
  'group/row flex w-full items-center gap-2 rounded-md px-2 py-[0.3125rem] text-left text-base font-medium ' +
  'text-fg-muted outline-offset-0 transition-[background-color,color] duration-[120ms] hover:bg-hover hover:text-fg';
export const ROW_CURRENT = 'bg-active text-fg';
const RAIL = 'justify-center px-0 py-[0.4375rem]';

function RowIcon({ icon: Icon }: { icon: LucideIcon }) {
  return <Icon aria-hidden className="size-4 shrink-0" strokeWidth={1.5} />;
}

/** A destination: icon and label, or the icon alone on the collapsed rail. */
function ItemLink({
  item,
  current,
  collapsed,
  className,
}: {
  item: NavItem;
  current: boolean;
  collapsed: boolean;
  className?: string;
}) {
  const link = (
    <Link
      to={item.to}
      aria-current={current ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(ROW, collapsed && RAIL, current && ROW_CURRENT, className)}
    >
      <RowIcon icon={item.icon} />
      {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
    </Link>
  );
  return collapsed ? (
    <Tooltip content={item.label} side="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

/** A group's row and, under it, its destinations, indented with their icons. */
function Group({
  section,
  pathname,
  open,
  onToggle,
}: {
  section: NavGroup;
  pathname: string;
  open: boolean;
  onToggle: () => void;
}) {
  const holdsCurrent = section.items.some((item) => isActivePath(pathname, item.to));
  const panelId = `nav-group-${section.id}`;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="flex flex-col gap-px">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(ROW, holdsCurrent && !open && ROW_CURRENT)}
      >
        <RowIcon icon={section.icon} />
        <span className="min-w-0 flex-1 truncate">{section.label}</span>
        <Chevron aria-hidden className="size-3 shrink-0 text-fg-subtle" strokeWidth={1.75} />
      </button>
      <div
        id={panelId}
        inert={!open}
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-[160ms] ease-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-px ps-3">
            {section.items.map((item) => (
              <ItemLink
                key={item.to}
                item={item}
                current={isActivePath(pathname, item.to)}
                collapsed={false}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** On the collapsed rail a group is its icon, and its destinations open beside it. */
function RailGroup({ section, pathname }: { section: NavGroup; pathname: string }) {
  const navigate = useNavigate();
  const holdsCurrent = section.items.some((item) => isActivePath(pathname, item.to));
  return (
    <DropdownMenu>
      <Tooltip content={section.label} side="right">
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={section.label}
            className={cn(
              ROW,
              RAIL,
              holdsCurrent && ROW_CURRENT,
              'data-[state=open]:bg-active data-[state=open]:text-fg',
            )}
          >
            <RowIcon icon={section.icon} />
          </button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent side="right" align="start" sideOffset={10} className="min-w-[13rem]">
        <DropdownMenuLabel>{section.label}</DropdownMenuLabel>
        {section.items.map((item) => {
          const Icon = item.icon;
          const current = isActivePath(pathname, item.to);
          return (
            <DropdownMenuItem
              key={item.to}
              onSelect={() => void navigate(item.to)}
              className={cn(current && 'bg-selected !text-fg')}
            >
              <Icon aria-hidden strokeWidth={1.5} />
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The rail: clear glass on the page ground, the brand and the collapse
 * control, the search field, the menu, then Settings and the account row at
 * the foot. A round handle on its edge appears while the pointer is over it.
 */
export function Sidebar() {
  const { sidebarCollapsed: collapsed, navOpen } = useUi();
  const { can } = useSession();
  const { pathname } = useLocation();
  const { darkRail } = useTheme();

  const sections = allowedSections(NAVIGATION, can);
  const settingsTo = firstSettingsDestination(can);
  const inSettings = activeSection(SETTINGS_NAVIGATION, pathname) !== undefined;

  // The group holding the current page is open while the viewer is in it,
  // unless they closed it; a group they opened stays open (`navOpen`).
  const currentGroupId = activeSection(sections.filter(isGroup), pathname)?.id ?? null;
  const [closedCurrent, setClosedCurrent] = useState<string | null>(null);
  if (closedCurrent !== null && closedCurrent !== currentGroupId) setClosedCurrent(null);

  const isOpen = (id: string) => navOpen.includes(id) || (id === currentGroupId && closedCurrent !== id);
  const toggle = (id: string) => {
    const open = isOpen(id);
    if (navOpen.includes(id) || !open) ui.toggleNavGroup(id);
    if (id === currentGroupId) setClosedCurrent(open ? id : null);
  };

  const collapseLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <aside
      aria-label="Primary"
      className={cn(
        'group/aside relative z-10 flex shrink-0 flex-col rail transition-[width,box-shadow] duration-[180ms] ease-out',
        '[@media(hover:hover)]:hover:shadow-[var(--bp-sidebar-haze)]',
        darkRail && 'dark-scope rail-dark',
        collapsed
          ? 'w-[var(--sidebar-w-collapsed)]'
          : 'w-[var(--sidebar-w)] [@media(min-width:64rem)_and_(max-width:95.99rem)]:w-[12.5rem]',
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto px-2 py-2.5">
        {/* The brand row, with the collapse control beside it. */}
        <div className={cn('flex items-center gap-0.5', collapsed && 'flex-col')}>
          <Link
            to="/"
            aria-label="Biller home"
            className={cn(
              'flex min-w-0 items-center rounded-md px-2 py-1.5 hover:bg-hover',
              collapsed ? 'justify-center' : 'flex-1',
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <LogoMark />
              {!collapsed && (
                <span className="truncate text-xs font-semibold tracking-[0.08em] text-fg uppercase">
                  Biller
                </span>
              )}
            </span>
          </Link>
          <Tooltip
            content={
              <span className="flex items-center gap-1.5">
                {collapseLabel} <Kbd>[</Kbd>
              </span>
            }
            side="right"
          >
            <button
              type="button"
              onClick={ui.toggleSidebar}
              aria-label={collapseLabel}
              aria-expanded={!collapsed}
              className="grid size-7 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-hover hover:text-fg"
            >
              <PanelLeft aria-hidden className="size-4" strokeWidth={1.5} />
            </button>
          </Tooltip>
        </div>

        {/* Search opens the palette. */}
        {collapsed ? (
          <Tooltip
            content={
              <span className="flex items-center gap-1.5">
                Search <ModKey />
                <Kbd>K</Kbd>
              </span>
            }
            side="right"
          >
            <button
              type="button"
              aria-label="Search"
              onClick={() => ui.setPaletteOpen(true)}
              className={cn(ROW, RAIL)}
            >
              <Search aria-hidden className="size-4 shrink-0" strokeWidth={1.5} />
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => ui.setPaletteOpen(true)}
            className={cn(
              'flex h-7 w-full items-center gap-2 rounded-md border border-border-strong bg-[var(--bp-field-fill)] px-2 text-base text-fg-subtle',
              'transition-[border-color,color] duration-[120ms] hover:border-fg-subtle hover:text-fg-muted',
            )}
          >
            <Search aria-hidden className="size-4 shrink-0" strokeWidth={1.5} />
            <span className="flex-1 truncate text-left">Search</span>
            <span className="flex shrink-0 items-center gap-0.5">
              <ModKey />
              <Kbd>K</Kbd>
            </span>
          </button>
        )}

        <nav aria-label="Sidebar" className={cn('flex flex-1 flex-col gap-px', collapsed && 'items-center')}>
          {sections.map((section, i) => {
            const group = isGroup(section);
            const previous = sections[i - 1];
            // Pinned runs are set apart by a rule; the groups start under one too.
            const rule = i > 0 && (group ? previous !== undefined && !isGroup(previous) : true);
            return (
              <Fragment key={section.id}>
                {rule && (
                  <div
                    aria-hidden
                    className={cn('my-2 h-px shrink-0 bg-border-strong', collapsed ? 'w-5' : 'mx-2.5')}
                  />
                )}
                <div className={cn('flex flex-col gap-px', collapsed && 'w-full items-center')}>
                  {!group ? (
                    section.items.map((item) => (
                      <ItemLink
                        key={item.to}
                        item={item}
                        current={isActivePath(pathname, item.to)}
                        collapsed={collapsed}
                      />
                    ))
                  ) : collapsed ? (
                    <RailGroup section={section} pathname={pathname} />
                  ) : (
                    <Group
                      section={section}
                      pathname={pathname}
                      open={isOpen(section.id)}
                      onToggle={() => toggle(section.id)}
                    />
                  )}
                </div>
              </Fragment>
            );
          })}
        </nav>

        {/* The foot: Settings, then who is signed in. */}
        <div className={cn('flex flex-col gap-px pt-1', collapsed && 'items-center')}>
          {settingsTo && (
            <ItemLink
              item={{ label: 'Settings', to: settingsTo, icon: Settings }}
              current={inSettings}
              collapsed={collapsed}
            />
          )}
          <AccountMenu collapsed={collapsed} />
        </div>
      </div>

      {/* A round handle on the edge, shown while the pointer is over the rail. */}
      <button
        type="button"
        tabIndex={-1}
        onClick={ui.toggleSidebar}
        aria-label={collapseLabel}
        aria-expanded={!collapsed}
        className={cn(
          'absolute top-1/2 -right-3.5 z-10 hidden size-7 -translate-y-1/2 place-items-center rounded-full',
          'border border-[var(--bp-glow-color)] bg-[var(--bp-glow-color)] text-fg shadow-[var(--bp-edge-glow)]',
          'opacity-0 transition-opacity duration-[150ms] group-hover/aside:opacity-100 focus-visible:opacity-100',
          '[@media(min-width:64rem)_and_(hover:hover)]:grid',
        )}
      >
        {collapsed ? (
          <ChevronRight aria-hidden className="size-3.5" strokeWidth={1.75} />
        ) : (
          <ChevronLeft aria-hidden className="size-3.5" strokeWidth={1.75} />
        )}
      </button>
    </aside>
  );
}
